import dayjs from 'dayjs';
import { NfcCard, Visitor } from 'graphql/generated/graphqlTypes';
import {
  getVisitorStatuses,
  isVisitorExpiresIn,
  isVisitorHasAdministratorRole,
  isVisitorHasCard,
  isVisitorHasInstallerRole,
  isVisitorHasKeyManagerRole,
  isVisitorHasNoCards,
  isVisitorWithoutGroup,
  Statuses
} from 'utils/Visitor/visitor';
import { getVisitorOrigin, VisitorOrigin } from 'v2/utils/visitorOrigin';
import { hasEnabledFilter } from './userFilterConstants';
import { FilterAccess, FilterOrigin, FilterRole, FilterUserExpires, FilterUserExpiresState } from './userFilterTypes';

interface Params {
  visitors: Visitor[];
  cards: NfcCard[];
  filterAccess: FilterAccess;
  filterUserExpires: FilterUserExpiresState;
  filterRole: FilterRole;
  filterOrigin: FilterOrigin;
}

export const filterVisitors = ({
  visitors,
  cards,
  filterAccess,
  filterUserExpires,
  filterRole,
  filterOrigin
}: Params) => {
  let data = visitors;

  if (hasEnabledFilter(filterAccess)) {
    data = data.filter((visitor) => {
      const status = getVisitorStatuses(visitor, cards);

      const isConnectionFilterEnabled = filterAccess.accessAppDisconnected || filterAccess.configAppDisconnected;
      const matchesConnection =
        !isConnectionFilterEnabled ||
        (filterAccess.accessAppDisconnected &&
          (status === Statuses.isAccessDeviceDisconnectedAndVisitorHasNoCards ||
            status === Statuses.isAccessDeviceDisconnectedAndVisitorHasCards)) ||
        (filterAccess.configAppDisconnected && status === Statuses.isConfigDeviceDisconnected);

      const isCardFilterEnabled = filterAccess.hasCard || filterAccess.noCard;
      const matchesCard =
        !isCardFilterEnabled ||
        (filterAccess.hasCard && isVisitorHasCard(visitor, cards)) ||
        (filterAccess.noCard && isVisitorHasNoCards(visitor, cards));

      const matchesGroup = !filterAccess.noGroup || isVisitorWithoutGroup(visitor);

      return matchesConnection && matchesCard && matchesGroup;
    });
  }

  if (filterUserExpires !== null) {
    data = data.filter((visitor) => {
      switch (filterUserExpires) {
        case FilterUserExpires.Expired:
          return Boolean(visitor.expirationDate && dayjs(visitor.expirationDate).isBefore(dayjs()));
        case FilterUserExpires.OneDay:
          return isVisitorExpiresIn(visitor, 1, 'day');
        case FilterUserExpires.OneWeek:
          return isVisitorExpiresIn(visitor, 1, 'week');
        default: {
          const exhaustiveCheck: never = filterUserExpires;
          return exhaustiveCheck;
        }
      }
    });
  }

  if (hasEnabledFilter(filterRole)) {
    data = data.filter(
      (visitor) =>
        (filterRole.keyManager && isVisitorHasKeyManagerRole(visitor)) ||
        (filterRole.administrator && isVisitorHasAdministratorRole(visitor)) ||
        (filterRole.installer && isVisitorHasInstallerRole(visitor))
    );
  }

  if (hasEnabledFilter(filterOrigin)) {
    data = data.filter(
      (visitor) =>
        (filterOrigin.entraId && getVisitorOrigin(visitor) === VisitorOrigin.EntraId) ||
        (filterOrigin.atlas && getVisitorOrigin(visitor) === VisitorOrigin.Atlas) ||
        (filterOrigin.keySharing && getVisitorOrigin(visitor) === VisitorOrigin.KeySharing)
    );
  }

  return data;
};
